import React, { useState, useMemo, useEffect } from 'react';
import { PriceTrackerProductData } from '../types';
import { BarChart } from './charts/BarChart';
import { LineChart } from './charts/LineChart';
import { apiService } from '../services/apiService';
import { Spinner } from './Spinner';

interface PriceTrackerProps {
  dateFrom: string | null;
  dateTo: string | null;
}

export const PriceTracker: React.FC<PriceTrackerProps> = ({ dateFrom, dateTo }) => {
    const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
    const [uniqueProducts, setUniqueProducts] = useState<string[]>([]);
    const [productData, setProductData] = useState<PriceTrackerProductData | null>(null);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProducts = async () => {
            setIsLoadingProducts(true);
            setError(null);
            try {
                const products = await apiService.getPriceTrackerUniqueProducts(dateFrom, dateTo);
                setUniqueProducts(products);
            } catch (e: any) {
                setError('Nepodařilo se načíst seznam produktů.');
            } finally {
                setIsLoadingProducts(false);
            }
        };
        fetchProducts();
    }, [dateFrom, dateTo]);
    
    useEffect(() => {
        if (!selectedProduct) {
            setProductData(null);
            return;
        }

        const fetchProductData = async () => {
            setIsLoadingData(true);
            setError(null);
            try {
                const data = await apiService.getPriceTrackerProductData(dateFrom, dateTo, selectedProduct);
                setProductData(data);
            } catch (e: any) {
                setError(`Nepodařilo se načíst data pro produkt "${selectedProduct}".`);
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchProductData();
    }, [selectedProduct, dateFrom, dateTo]);


    if (isLoadingProducts) {
        return <div className="flex justify-center py-10"><Spinner text="Načítám seznam produktů..." /></div>;
    }

    if (error) {
        return <div className="text-center text-red-500 py-10">{error}</div>;
    }
    
    if (uniqueProducts.length === 0) {
        return <div className="text-center text-gray-500 py-10">Pro vybrané období nebyly na účtenkách nalezeny žádné detailní položky pro sledování.</div>;
    }

    return (
        <div className="space-y-6">
            <div>
                <label htmlFor="product-select" className="block text-sm font-medium text-gray-700 mb-1">Vyberte produkt pro analýzu:</label>
                <select 
                    id="product-select"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    onChange={(e) => setSelectedProduct(e.target.value || null)}
                    value={selectedProduct || ''}
                >
                    <option value="">-- Vyberte produkt --</option>
                    {uniqueProducts.map(product => (
                        <option key={product} value={product}>{product}</option>
                    ))}
                </select>
            </div>

            {!selectedProduct && (
                 <div className="text-center text-gray-500 py-10">Vyberte produkt ze seznamu výše pro zobrazení grafů.</div>
            )}
            
            {isLoadingData && (
                 <div className="flex justify-center py-10"><Spinner text={`Načítám data pro ${selectedProduct}...`} /></div>
            )}

            {!isLoadingData && selectedProduct && productData && (
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-xl font-semibold text-gray-700 mb-4">Porovnání průměrné ceny v obchodech</h3>
                        {productData.storeComparisonData.length > 0 ? <BarChart data={productData.storeComparisonData} /> : <p className="text-center text-gray-500">Nedostatek dat pro porovnání.</p>}
                    </div>
                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h3 className="text-xl font-semibold text-gray-700 mb-4">Vývoj ceny v čase</h3>
                        {productData.timeEvolutionData.length > 1 ? <LineChart data={productData.timeEvolutionData} /> : <p className="text-center text-gray-500">Pro zobrazení vývoje ceny jsou potřeba alespoň dva záznamy.</p>}
                    </div>
                 </div>
            )}
        </div>
    );
};